import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, MessageCircle, User, Clock, CheckCircle, AlertCircle, Eye, Search, Download, Plus, Smartphone, Calendar, X, Users, FileText } from 'lucide-react';
import { whatsappService } from '../../../../services/whatsappService';

const MessageCenter = () => {
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    message_type: 'all',
    date_from: '',
    date_to: '',
    search: ''
  });

  // Send message form
  const [sendForm, setSendForm] = useState({
    phone_number: '',
    message_content: '',
    message_type: 'custom',
    template_id: ''
  });

  const messageStatuses = [
    { value: 'all', label: 'All Messages', count: 0 },
    { value: 'pending', label: 'Pending', count: 0, color: 'text-yellow-400' },
    { value: 'sent', label: 'Sent', count: 0, color: 'text-blue-400' },
    { value: 'delivered', label: 'Delivered', count: 0, color: 'text-green-400' },
    { value: 'read', label: 'Read', count: 0, color: 'text-purple-400' },
    { value: 'failed', label: 'Failed', count: 0, color: 'text-red-400' }
  ];

  useEffect(() => {
    loadInitialData();
    setupRealtimeSubscription();
  }, []);

  useEffect(() => {
    loadMessages();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load messages and templates
      const [messagesResult, templatesResult] = await Promise.all([
        whatsappService?.getMessages(),
        whatsappService?.getMessageTemplates()
      ]);

      setMessages(messagesResult?.data || []);
      setTemplates(templatesResult?.data || []);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const { data } = await whatsappService?.getMessages(filters);
      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const subscription = whatsappService?.subscribeToMessages((payload) => {
      if (payload?.eventType === 'INSERT') {
        setMessages(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setMessages(prev => prev?.map(msg =>
          msg?.id === payload?.new?.id ? payload?.new : msg
        ));
      }
    });

    return () => subscription?.unsubscribe();
  };

  const handleSendMessage = async () => {
    if (!sendForm?.phone_number || !sendForm?.message_content) return;

    setSendingMessage(true);
    try {
      const { data, error } = await whatsappService?.sendMessage({
        phone_number: sendForm?.phone_number,
        message_content: sendForm?.message_content,
        message_type: sendForm?.message_type,
        template_id: sendForm?.template_id || null
      });

      if (!error) {
        setShowSendModal(false);
        setSendForm({
          phone_number: '',
          message_content: '',
          message_type: 'custom',
          template_id: ''
        });
        await loadMessages();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates?.find(t => t?.id === templateId);
    if (template) {
      setSendForm(prev => ({
        ...prev,
        template_id: templateId,
        message_content: template?.template_text,
        message_type: template?.message_type
      }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'read': return <Eye className="w-4 h-4" />;
      case 'failed': return <AlertCircle className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-500/20';
      case 'sent': return 'text-blue-400 bg-blue-500/20';
      case 'delivered': return 'text-green-400 bg-green-500/20';
      case 'read': return 'text-purple-400 bg-purple-500/20';
      case 'failed': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'appointment_confirmation': return <Calendar className="w-4 h-4" />;
      case 'appointment_reminder': return <Clock className="w-4 h-4" />;
      case 'bulk': return <Users className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  // Calculate status counts
  const statusCounts = messageStatuses?.map(status => ({
    ...status,
    count: status?.value === 'all'
      ? messages?.length
      : messages?.filter(msg => msg?.status === status?.value)?.length
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
            <MessageCircle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Message Center</h2>
            <p className="text-gray-400">Send and manage WhatsApp messages</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => {/* Export functionality */ }}
            className="px-4 py-2 text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500 rounded-xl transition-colors flex items-center space-x-2"
          >
            <Download className="w-5 h-5" />
            <span>Export</span>
          </button>

          <button
            onClick={() => setShowSendModal(true)}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Send Message</span>
          </button>
        </div>
      </div>
      {/* Status Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
      >
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {statusCounts?.map((status) => (
            <button
              key={status?.value}
              onClick={() => setFilters(prev => ({ ...prev, status: status?.value }))}
              className={`p-4 rounded-xl border-2 transition-all ${filters?.status === status?.value
                  ? 'border-blue-500 bg-blue-500/20' : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
            >
              <div className="text-2xl font-bold text-white mb-1">{status?.count}</div>
              <div className={`text-sm ${status?.color || 'text-gray-400'}`}>
                {status?.label}
              </div>
            </button>
          ))}
        </div>
      </motion.div>
      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search messages..."
              value={filters?.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e?.target?.value }))}
              className="pl-10 pr-4 py-3 w-full bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <select
            value={filters?.message_type}
            onChange={(e) => setFilters(prev => ({ ...prev, message_type: e?.target?.value }))}
            className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            <option value="all">All Types</option>
            <option value="appointment_confirmation">Confirmations</option>
            <option value="appointment_reminder">Reminders</option>
            <option value="custom">Custom</option>
            <option value="bulk">Bulk</option>
          </select>

          <input
            type="date"
            value={filters?.date_from}
            onChange={(e) => setFilters(prev => ({ ...prev, date_from: e?.target?.value }))}
            className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />

          <input
            type="date"
            value={filters?.date_to}
            onChange={(e) => setFilters(prev => ({ ...prev, date_to: e?.target?.value }))}
            className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
      </motion.div>
      {/* Messages List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden"
      >
        <div className="p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Recent Messages</h3>
        </div>

        <div className="divide-y divide-white/10">
          {messages?.length > 0 ? (
            messages?.map((message) => (
              <motion.div
                key={message?.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                      {message?.recipient?.full_name?.charAt(0) || 'U'}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-medium text-white">
                          {message?.recipient?.full_name || 'Unknown User'}
                        </h4>
                        <span className="text-sm text-gray-400">
                          {message?.recipient?.phone || message?.phone_number}
                        </span>
                        <div className="flex items-center space-x-1 text-gray-400">
                          {getMessageTypeIcon(message?.message_type)}
                          <span className="text-sm capitalize">
                            {message?.message_type?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>

                      <p className="text-gray-300 text-sm line-clamp-2 mb-3">
                        {message?.message_content}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>Sent: {new Date(message.created_at)?.toLocaleString()}</span>
                          {message?.delivery_timestamp && (
                            <span>Delivered: {new Date(message.delivery_timestamp)?.toLocaleString()}</span>
                          )}
                        </div>

                        <button
                          onClick={() => setSelectedMessage(message)}
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(message?.status)}`}>
                      {getStatusIcon(message?.status)}
                      <span>{message?.status?.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="p-12 text-center">
              <MessageCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No messages found</h3>
              <p className="text-gray-400 mb-6">Send your first WhatsApp message to get started</p>
              <button
                onClick={() => setShowSendModal(true)}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors"
              >
                Send Message
              </button>
            </div>
          )}
        </div>
      </motion.div>
      {/* Send Message Modal */}
      <AnimatePresence>
        {showSendModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gray-900 rounded-2xl border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Send WhatsApp Message</h3>
                  <button
                    onClick={() => setShowSendModal(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Smartphone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="tel"
                        value={sendForm?.phone_number}
                        onChange={(e) => setSendForm(prev => ({ ...prev, phone_number: e?.target?.value }))}
                        className="pl-10 pr-4 py-3 w-full rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        placeholder="+1234567890"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message Type
                    </label>
                    <select
                      value={sendForm?.message_type}
                      onChange={(e) => setSendForm(prev => ({ ...prev, message_type: e?.target?.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    >
                      <option value="custom">Custom Message</option>
                      <option value="appointment_confirmation">Appointment Confirmation</option>
                      <option value="appointment_reminder">Appointment Reminder</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Use Template (Optional)
                  </label>
                  <select
                    value={sendForm?.template_id}
                    onChange={(e) => handleTemplateSelect(e?.target?.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">Select a template...</option>
                    {templates?.filter(t => t?.is_active)?.map(template => (
                      <option key={template?.id} value={template?.id}>
                        {template?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message Content
                  </label>
                  <textarea
                    value={sendForm?.message_content}
                    onChange={(e) => setSendForm(prev => ({ ...prev, message_content: e?.target?.value }))}
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    placeholder="Enter your message here..."
                  />
                  <div className="mt-2 text-sm text-gray-400">
                    {sendForm?.message_content?.length} characters
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Message Preview:</h4>
                  <div className="bg-green-600 text-white p-3 rounded-2xl rounded-bl-none max-w-xs">
                    {sendForm?.message_content || 'Your message will appear here...'}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex justify-end space-x-4">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !sendForm?.phone_number || !sendForm?.message_content}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
                >
                  {sendingMessage ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Message Details Modal */}
      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gray-900 rounded-2xl border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Message Details</h3>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-semibold">
                    {selectedMessage?.recipient?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-white">
                      {selectedMessage?.recipient?.full_name || 'Unknown User'}
                    </h4>
                    <p className="text-gray-400">{selectedMessage?.phone_number}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Status</label>
                    <div className={`mt-1 px-3 py-1 rounded-full text-sm font-medium inline-flex items-center space-x-1 ${getStatusColor(selectedMessage?.status)}`}>
                      {getStatusIcon(selectedMessage?.status)}
                      <span>{selectedMessage?.status?.toUpperCase()}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Type</label>
                    <div className="mt-1 text-white capitalize">
                      {selectedMessage?.message_type?.replace('_', ' ')}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400">Message Content</label>
                  <div className="mt-2 p-4 bg-white/5 rounded-xl border border-white/10 text-white">
                    {selectedMessage?.message_content}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-gray-400">Created</label>
                    <div className="text-white">
                      {new Date(selectedMessage.created_at)?.toLocaleString()}
                    </div>
                  </div>
                  {selectedMessage?.delivery_timestamp && (
                    <div>
                      <label className="text-gray-400">Delivered</label>
                      <div className="text-white">
                        {new Date(selectedMessage.delivery_timestamp)?.toLocaleString()}
                      </div>
                    </div>
                  )}
                  {selectedMessage?.read_timestamp && (
                    <div>
                      <label className="text-gray-400">Read</label>
                      <div className="text-white">
                        {new Date(selectedMessage.read_timestamp)?.toLocaleString()}
                      </div>
                    </div>
                  )}
                  {selectedMessage?.whatsapp_message_id && (
                    <div>
                      <label className="text-gray-400">WhatsApp ID</label>
                      <div className="text-white font-mono text-xs">
                        {selectedMessage?.whatsapp_message_id}
                      </div>
                    </div>
                  )}
                </div>

                {selectedMessage?.error_message && (
                  <div>
                    <label className="text-sm text-red-400">Error Message</label>
                    <div className="mt-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
                      {selectedMessage?.error_message}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageCenter;